/**
 * Uploading a file into a shared folder (P1-F4, plan P1-B5). The member's own credentials never
 * reach the owner's space: the sharing API signs every request with a one-folder session (E4, E5),
 * and the browser sends the bytes straight to S3 with what it was given.
 *
 * - Up to 16 MB: one presigned POST, its `fields` sent exactly as returned.
 * - Larger: a multipart upload, 4 parts in flight, part URLs signed in batches, the `ETag` of each
 *   part read from S3's response (the bucket's CORS exposes it), then completed.
 *
 * Cancelling (the caller's `AbortSignal`) stops the requests and aborts the multipart upload on the
 * server, as does any failure. Closing the tab asks first (`guardLeave`); if the user leaves anyway,
 * the upload is aborted on a best-effort `keepalive` request, and S3's lifecycle rule removes what is
 * left after 10 days.
 */
import axios, {AxiosProgressEvent} from 'axios'
import {API, Auth} from 'aws-amplify'
import {guardLeave} from '@/shared/utils/leaveGuard'
import {
  abortShareMultipart,
  completeShareMultipart,
  initiateShareMultipart,
  requestSharePartUrls,
  requestShareUploadUrl,
  SHARE_MAX_PARTS_PER_CALL,
  SHARE_SINGLE_UPLOAD_MAX_BYTES,
} from './shareFilesApi'

export interface ShareUploadOptions {
  /** Bytes sent so far, of `total`. Called often; the last call has `loaded === total`. */
  onProgress?: (loaded: number, total: number) => void
  /** Cancels the upload. The promise then rejects with {@link ShareUploadAbortedError}. */
  signal?: AbortSignal
}

/** Sizes and limits, overridable so tests can use small files. */
export interface ShareUploadLimits {
  /** Files up to this size go in one POST (the server's limit is 16 MB). */
  singleUploadMaxBytes: number
  /** Smallest part (S3 needs at least 5 MiB for every part but the last). */
  partSize: number
  /** Parts in flight at once. */
  concurrency: number
  /** Part URLs asked for per call: used soon after they are signed (they last 15 minutes). */
  partUrlBatch: number
}

export const DEFAULT_SHARE_UPLOAD_LIMITS: ShareUploadLimits = {
  singleUploadMaxBytes: SHARE_SINGLE_UPLOAD_MAX_BYTES,
  partSize: 8 * 1024 * 1024,
  concurrency: 4,
  partUrlBatch: 20,
}

/** S3 allows at most this many parts per upload. */
const S3_MAX_PARTS = 10000
/** Tries per part, each with a freshly signed URL after the first. */
const PART_ATTEMPTS = 3

const apiName: string = process.env.REACT_APP_API_NAME || 'JobService'

/** The user cancelled the upload. Nothing was kept. */
export class ShareUploadAbortedError extends Error {
  constructor() {
    super('The upload was cancelled.')
    this.name = 'ShareUploadAbortedError'
  }
}

export const isShareUploadAborted = (error: unknown): error is ShareUploadAbortedError =>
  error instanceof ShareUploadAbortedError

/**
 * Presigned S3 requests carry their signature in the URL or form. A client of their own, so no
 * interceptor or default header the app adds to its API calls can ever be attached to them.
 * Exported for tests, which stand in for S3 by replacing its `post` and `put`.
 */
export const shareS3Client = axios.create()
const s3Client = shareS3Client

/** The part count and size for a file: at least `minPartSize`, at most {@link S3_MAX_PARTS} parts. */
export function planParts(size: number, minPartSize: number): {partSize: number; count: number} {
  const partSize = Math.max(minPartSize, Math.ceil(size / S3_MAX_PARTS))
  return {partSize, count: Math.max(1, Math.ceil(size / partSize))}
}

/** Uploads `file` to `path` (relative to the shared folder) in share `shareId`. */
export function uploadFileToShare(
  shareId: string,
  path: string,
  file: File,
  options: ShareUploadOptions = {},
  limits: ShareUploadLimits = DEFAULT_SHARE_UPLOAD_LIMITS
): Promise<void> {
  if (options.signal?.aborted) {
    return Promise.reject(new ShareUploadAbortedError())
  }
  // Closing the tab would lose the upload: ask first, as for uploads to My Files.
  return guardLeave(() =>
    file.size <= limits.singleUploadMaxBytes
      ? uploadInOnePost(shareId, path, file, options)
      : uploadInParts(shareId, path, file, options, limits)
  )
}

const isCancel = (error: unknown) => axios.isCancel(error)

async function uploadInOnePost(
  shareId: string,
  path: string,
  file: File,
  {onProgress, signal}: ShareUploadOptions
): Promise<void> {
  const presigned = await requestShareUploadUrl(shareId, path)
  if (signal?.aborted) {
    throw new ShareUploadAbortedError()
  }
  const form = new FormData()
  Object.entries(presigned.fields ?? {}).forEach(([name, value]) => form.append(name, value))
  // The policy requires a Content-Type field (any value); the file must be the last field.
  form.append('Content-Type', file.type || 'application/octet-stream')
  form.append('file', file)
  try {
    await s3Client.post(presigned.url, form, {
      signal,
      onUploadProgress: (event: AxiosProgressEvent) =>
        onProgress?.(Math.min(event.loaded, file.size), file.size),
    })
  } catch (error) {
    if (isCancel(error) || signal?.aborted) {
      throw new ShareUploadAbortedError()
    }
    throw error
  }
  onProgress?.(file.size, file.size)
}

async function uploadInParts(
  shareId: string,
  path: string,
  file: File,
  {onProgress, signal}: ShareUploadOptions,
  limits: ShareUploadLimits
): Promise<void> {
  const total = file.size
  const {partSize, count} = planParts(total, limits.partSize)
  const batchSize = Math.max(1, Math.min(limits.partUrlBatch, SHARE_MAX_PARTS_PER_CALL))

  const {uploadId} = await initiateShareMultipart(shareId, path, file.type || undefined)
  const active = trackActiveUpload({shareId, path, uploadId})

  // One controller for every request of this upload: the caller's cancel, or the first part that
  // fails for good, stops the others.
  const controller = new AbortController()
  const onAbort = () => controller.abort()
  signal?.addEventListener('abort', onAbort)
  if (signal?.aborted) {
    // Cancelled while the upload was being started.
    controller.abort()
  }

  const etags: string[] = new Array(count)
  const sent: number[] = new Array(count).fill(0)
  const report = () =>
    onProgress?.(
      sent.reduce((sum, n) => sum + n, 0),
      total
    )

  // Part URLs, signed a batch at a time as the upload reaches them.
  const urls = new Map<number, string>()
  const batches = new Map<number, Promise<void>>()
  const signBatch = (first: number) => {
    let pending = batches.get(first)
    if (!pending) {
      const numbers: number[] = []
      for (let n = first; n < first + batchSize && n <= count; n++) numbers.push(n)
      pending = requestSharePartUrls(shareId, path, uploadId, numbers).then(({urls: signed}) => {
        Object.entries(signed ?? {}).forEach(([n, url]) => urls.set(Number(n), url))
        void active.refreshAuth()
      })
      batches.set(first, pending)
    }
    return pending
  }
  const partUrl = async (n: number, fresh: boolean): Promise<string> => {
    if (fresh) {
      const {urls: signed} = await requestSharePartUrls(shareId, path, uploadId, [n])
      const url = signed?.[String(n)]
      if (!url) throw new Error(`No upload URL for part ${n}.`)
      return url
    }
    const first = Math.floor((n - 1) / batchSize) * batchSize + 1
    await signBatch(first)
    const url = urls.get(n)
    urls.delete(n)
    if (!url) throw new Error(`No upload URL for part ${n}.`)
    return url
  }

  const uploadPart = async (n: number): Promise<string> => {
    const start = (n - 1) * partSize
    const blob = file.slice(start, Math.min(start + partSize, total))
    for (let attempt = 1; ; attempt++) {
      const url = await partUrl(n, attempt > 1)
      try {
        const response = await s3Client.put(url, blob, {
          signal: controller.signal,
          onUploadProgress: (event: AxiosProgressEvent) => {
            sent[n - 1] = Math.min(event.loaded, blob.size)
            report()
          },
        })
        const etag = readEtag(response.headers)
        if (!etag) {
          // Without it the upload can't be completed. The bucket's CORS must expose ETag.
          throw new Error(`S3 returned no ETag for part ${n}.`)
        }
        return etag
      } catch (error) {
        sent[n - 1] = 0
        if (controller.signal.aborted || attempt >= PART_ATTEMPTS || !isRetryable(error)) {
          throw error
        }
      }
    }
  }

  let next = 1
  const worker = async () => {
    while (next <= count && !controller.signal.aborted) {
      const n = next++
      try {
        etags[n - 1] = await uploadPart(n)
      } catch (error) {
        controller.abort()
        throw error
      }
      sent[n - 1] = Math.min(partSize, total - (n - 1) * partSize)
      report()
    }
  }

  try {
    const workers = Array.from({length: Math.min(limits.concurrency, count)}, worker)
    // Wait for every part to settle, so nothing is still being written when the upload is aborted.
    const results = await Promise.allSettled(workers)
    if (signal?.aborted) {
      throw new ShareUploadAbortedError()
    }
    const failure = results.find(
      (r): r is PromiseRejectedResult => r.status === 'rejected' && !isCancel(r.reason)
    )
    if (failure) {
      throw failure.reason
    }
    await completeShareMultipart(
      shareId,
      path,
      uploadId,
      etags.map((ETag, i) => ({PartNumber: i + 1, ETag}))
    )
    onProgress?.(total, total)
  } catch (error) {
    // Parts already stored would stay (and be billed to the owner) until S3's lifecycle rule
    // removes them: abort the upload on the server too.
    await abortShareMultipart(shareId, path, uploadId).catch(() => undefined)
    throw signal?.aborted || isCancel(error) ? new ShareUploadAbortedError() : error
  } finally {
    signal?.removeEventListener('abort', onAbort)
    active.done()
  }
}

function readEtag(headers: unknown): string | undefined {
  const h = headers as {get?: (name: string) => unknown; etag?: unknown} | undefined
  const value = typeof h?.get === 'function' ? h.get('etag') : h?.etag
  return typeof value === 'string' && value ? value : undefined
}

/** Network failures, S3 5xx, and an expired URL (403) are worth one more try. */
function isRetryable(error: unknown): boolean {
  if (isCancel(error)) return false
  const status = (error as {response?: {status?: number}})?.response?.status
  return status === undefined || status >= 500 || status === 403
}

/*
  Multipart uploads in progress, so they can be aborted if the page goes away. `pagehide` handlers
  can't wait for promises, so what the abort request needs (the API's URL and a sign-in token) is
  gathered while the upload runs, and the request is sent with `keepalive`.
*/
interface ActiveUpload {
  shareId: string
  path: string
  uploadId: string
  endpoint?: string
  authorization?: string
}

const activeUploads = new Set<ActiveUpload>()

function onPageHide() {
  activeUploads.forEach((upload) => {
    if (!upload.endpoint || !upload.authorization) return
    try {
      void fetch(
        `${upload.endpoint.replace(/\/$/, '')}/v1/shares/${encodeURIComponent(
          upload.shareId
        )}/files/multipart-abort`,
        {
          method: 'POST',
          keepalive: true,
          headers: {'Content-Type': 'application/json', Authorization: upload.authorization},
          body: JSON.stringify({path: upload.path, uploadId: upload.uploadId}),
        }
      ).catch(() => undefined)
    } catch {
      // Best effort: the lifecycle rule cleans up anyway.
    }
  })
}

function trackActiveUpload(upload: ActiveUpload) {
  if (activeUploads.size === 0 && typeof window !== 'undefined') {
    window.addEventListener('pagehide', onPageHide)
  }
  activeUploads.add(upload)
  const refreshAuth = async () => {
    try {
      upload.endpoint = upload.endpoint ?? (await API.endpoint(apiName))
      const session = await Auth.currentSession()
      upload.authorization = `Bearer ${session.getIdToken().getJwtToken()}`
    } catch {
      // Not signed in through Amplify (tests): no abort on page exit.
    }
  }
  void refreshAuth()
  return {
    refreshAuth,
    done: () => {
      activeUploads.delete(upload)
      if (activeUploads.size === 0 && typeof window !== 'undefined') {
        window.removeEventListener('pagehide', onPageHide)
      }
    },
  }
}

/** Test seam: the multipart uploads the page would abort if it went away. */
export function activeShareUploadsForTest(): ReadonlyArray<Readonly<ActiveUpload>> {
  return Array.from(activeUploads)
}
